/** Normalized account role: user | intern | employer | admin */
export function getUserRole(user) {
  return String(user?.role || user?.typeofuser || 'user')
    .trim()
    .toLowerCase();
}

export function isEmployer(user) {
  const role = getUserRole(user);
  return role === 'employer' || role === 'admin';
}

export function isIntern(user) {
  return getUserRole(user) === 'intern';
}

export function isAdmin(user) {
  const role = getUserRole(user);
  return (
    role === 'admin' ||
    role === 'super admin' ||
    role === 'superadmin' ||
    user?.is_admin === 1 ||
    user?.is_admin === true
  );
}

/** Author or site admin can edit/delete a feed post. */
export function canManagePost(user, post) {
  if (!user?.id || !post) return false;
  if (Number(user.id) === Number(post.user_id)) return true;
  return isAdmin(user);
}

/** User / Intern can apply to job postings. */
export function canApplyForJobs(user) {
  const role = getUserRole(user);
  return role === 'user' || role === 'intern';
}

export const APPLICATION_STATUS_LABELS = {
  submitted: 'Submitted',
  viewed: 'Viewed',
  under_review: 'Under Review',
  shortlisted: 'Shortlisted',
  interview_scheduled: 'Interview Scheduled',
  offered: 'Offered',
  hired: 'Hired',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
};

export function applicationStatusLabel(status) {
  const key = String(status || 'submitted')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  return APPLICATION_STATUS_LABELS[key] || 'Submitted';
}

/** True when hiring_deadline (YYYY-MM-DD or parseable date) is after end of that local day. */
export function isHiringDeadlinePassed(deadline) {
  const raw = String(deadline || '').trim();
  if (!raw) return false;
  // Prefer YYYY-MM-DD end-of-day so timezone parsing matches the PHP check.
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  let end;
  if (m) {
    end = new Date(
      Number(m[1]),
      Number(m[2]) - 1,
      Number(m[3]),
      23,
      59,
      59,
      999
    );
  } else {
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return false;
    end = new Date(parsed);
    end.setHours(23, 59, 59, 999);
  }
  return Date.now() > end.getTime();
}

/** Employer manually tagged the hiring post as closed. */
export function isHiringMarkedClosed(post) {
  return Boolean(post?.hiring_closed);
}

/** Still open for applications (not closed by employer, deadline blank or future). */
export function isHiringAcceptingApplications(post) {
  if (post && typeof post.hiring_accepting === 'boolean') {
    return post.hiring_accepting;
  }
  if (isHiringMarkedClosed(post)) return false;
  return !isHiringDeadlinePassed(post?.hiring_deadline);
}
